import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function PartII() {
  const navigate = useNavigate();
  const [header, setHeader] = useState({
    dslNo: '',
    pslNo: '',
    nameUnit: '',
    state: '',
    district: '',
    town: '',
    tehsil: '',
    roSroCode: '',
    schemeCode: '',
    indCodeFrame: '',
    indCodeReturn: '',
    accountingYear: '',
    year: ''
  });

  useEffect(() => {
    try {
      const bs = localStorage.getItem('balanceSheetFormData');
      if (bs) {
        const d = JSON.parse(bs);
        setHeader({
          dslNo: d.dslNo || '',
          pslNo: d.pslNo || '',
          nameUnit: d.nameUnit || '',
          state: d.state || '',
          district: d.district || '',
          town: d.town || '',
          tehsil: d.tehsil || '',
          roSroCode: d.roSroCode || '',
          schemeCode: d.schemeCode || '2',
          indCodeFrame: d.industryCodeFrame || '',
          indCodeReturn: d.industryCodeReturn || '',
          accountingYear: d.accountingYear || '',
          year: d.year || '2022-23'
        });
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const months = useMemo(() => {
    // Build Apr-Mar based on start year from header.year like "2022-23"
    const y = (header.year || '').split('-')[0] || '2022';
    const start = parseInt(y, 10);
    const list = [
      ['Apr', start % 100], ['May', start % 100], ['Jun', start % 100], ['Jul', start % 100],
      ['Aug', start % 100], ['Sep', start % 100], ['Oct', start % 100], ['Nov', start % 100],
      ['Dec', start % 100], ['Jan', (start + 1) % 100], ['Feb', (start + 1) % 100], ['Mar', (start + 1) % 100]
    ];
    return list.map(([m, yy]) => `${m},${yy.toString().padStart(2, '0')}`);
  }, [header.year]);

  const [monthRows, setMonthRows] = useState({});
  const [ssoSignature, setSsoSignature] = useState('');
  const [ssoName, setSsoName] = useState('');

  const getAuthConfig = () => {
    if (!hasAuthSession()) return { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
    return getAuthHeaders();
  };

  // Helper function to get full signature URL
  const getSignatureUrl = (filename, baseUrl) => {
    if (!filename) return '';
    
    // If filename already contains http/https, return as is
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    
    // If baseUrl is provided, use it
    if (baseUrl) {
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      return `${cleanBaseUrl}/${filename}`;
    }
    
    // Extract base URL from API_URL and add /uploads/ path
    let baseUrlFromApi = API_URL;
    // Remove trailing slash if present
    baseUrlFromApi = baseUrlFromApi.endsWith('/') ? baseUrlFromApi.slice(0, -1) : baseUrlFromApi;
    // Add /uploads/ path
    return `${baseUrlFromApi}/uploads/${filename}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!header.dslNo || !header.year) return;
      try {
        showLoading(true);
        const payload = {
          dsl_no: header.dslNo,
          survey_year: (header.year || '').split('-')[0] || '2022'
        };
        const res = await axios.post(`${API_URL}api/labour-wages/get-data`, payload, getAuthConfig());
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        const map = {};
        data.forEach(item => {
          const m = item.month_name; // e.g., Apr,22
          if (!m) return;
          const mandaysWorked = (Number(item.mandays_male||0) + Number(item.mandays_female||0) + Number(item.mandays_manager_supervisor||0) + Number(item.mandays_other_employees||0)).toString();
          map[m] = {
            scheduled: item.schedule_working_days_for_workers?.toString() || '',
            mandaysWorked,
            lostAbsence: item.mandays_lost_due_to_absence?.toString() || '',
            firstDay: item.workers_first_day?.toString() || '',
            lastDay: item.workers_last_day?.toString() || '',
            accession: item.accession_during_month?.toString() || '',
            sepMonth: (Number(item.separation_due_to_death_or_retirement||0) + Number(item.separation_due_to_other_reasons||0)).toString(),
            sepDeath: item.separation_due_to_death_or_retirement?.toString() || '',
            sepOther: item.separation_due_to_other_reasons?.toString() || '',
            remarks: ''
          };
        });
        setMonthRows(map);
      } catch (e) {
        toast.error('Failed to load Part II data');
      } finally {
        showLoading(false);
      }
    };
    fetchData();
  }, [header.dslNo, header.year]);

  // Fetch SSO signature data
  useEffect(() => {
    const fetchSsoSignature = async () => {
      if (!header.dslNo || !header.year) return;
      try {
        const payload = {
          dsl_no: header.dslNo,
          survey_year: (header.year || '').split('-')[0] || '2022'
        };
        const res = await axios.post(`${API_URL}api/asi/blockMAndNForm`, payload, getAuthConfig());
        
        if (res.data && res.data.status && res.data.data && Array.isArray(res.data.data)) {
          const blockMData = res.data.data.find(item => item.block === 'M') || {};
          
          if (blockMData.signature_of_superintending_officer) {
            const signatureUrl = getSignatureUrl(
              blockMData.signature_of_superintending_officer,
              blockMData.baseUrlofUploadfile
            );
            setSsoSignature(signatureUrl);
          }
          
          if (blockMData.name_of_superintending_officer) {
            setSsoName(blockMData.name_of_superintending_officer);
          }
        }
      } catch (e) {
        // Silently fail - signature is optional
        console.log('SSO signature not found or API error:', e);
      }
    };
    fetchSsoSignature();
  }, [header.dslNo, header.year]);

  return (
    <>
      <style>
        {`
          .report-wrap { max-width: 1100px; margin: 0 auto; padding: 16px; }
          .header-strip { text-align:center; padding: 10px 12px; }
          .header-title { color: var(--orange, #ff7a00); font-weight: 700; }
          .header-sub { color: #222; font-weight: 600; margin-left: 8px; }
          .meta-strip { background:#f7f9fb; border:1px solid #e3e6ea; padding:8px 12px; display:flex; justify-content:space-between; align-items:center; font-size:12px; border-radius:4px; }
          .meta-item { font-weight:600; color:#333; }
          table.report { width:100%; border-collapse:collapse; }
          .report th, .report td { border:1px solid #333; padding:6px; font-size:11px; vertical-align:middle; }
          .section-heading { text-align:left; font-weight:600; background:#f4f6f8; }
          .no-border { border:none !important; }
          .muted { color:#555; }
          .confidential { position:absolute; top:8px; right:12px; font-size:11px; border:1px solid #333; padding:3px 8px; border-radius:3px; background:#ffffff; font-weight:600; }
          @media print {
            @page { size: A4 landscape; margin:10mm; }
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; left:0; top:0; width:100%; }
            .no-print { display: none !important; }
            .report-wrap { padding:0; }
          }
        `}
      </style>

      <div className="report-wrap">
        <GlobalLoading />
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="card mx-3 mb-3 print-area">
          <div className="card-body" style={{ position: 'relative' }}>
            <div className="confidential">CONFIDENTIAL</div>
        {/* Header formatted like sample */}
        <div className="header-strip">
          <span className="header-title">Annual Survey of Industries, Uttar Pradesh</span>
          <span className="header-sub">PART II: Mandays Worked, Absenteeism & Labour Turnover</span>
        </div>
        <div className="meta-strip" style={{ marginBottom: 10 }}>
          <div className="meta-item">DSL No.: {header.dslNo || '-'}</div>
          <div className="meta-item">Survey Year: {header.year || '-'}</div>
        </div>

        {/* Block 1 */}
        <table className="report">
          <tbody>
            <tr>
              <th colSpan="6" className="section-heading">block 1. Identification & Other Particulars</th>
            </tr>
            <tr>
              <td width="20%">1. Schedule Despatch No.</td>
              <td width="13%">{header.dslNo}</td>
              <td width="20%">9. RO/SRO Code</td>
              <td>{header.roSroCode || ''}</td>
              {/* <td width="20%">10. Type of organisation ( Code )</td> */}
              {/* <td width="13%"></td> */}
              <td width="20%">16. Signature.</td>
              <td width="14%">
                {ssoSignature ? (
                  <img 
                    src={ssoSignature} 
                    alt="SSO Signature" 
                    style={{ maxWidth: '100px', maxHeight: '50px', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  ''
                )}
              </td>
            </tr>
            <tr>
              <td>2. PSL No.</td>
              <td>{header.pslNo}</td>
              <td>10. Type of organisation ( Code )</td>
              {/* <td>11. CIN</td> */}
              <td></td>
              <td>17. Name of Scrutinizing Officer:</td>
              <td></td>
            </tr>
            <tr>
              <td>3. Scheme Code (Census-1, Sample-2)</td>
              <td>{header.schemeCode || '2'}</td>
              <td>11. CIN</td>
              {/* <td>12. Accounting Year ( MMYY to MMYY )</td> */}
              <td colSpan="3"></td>
              {/* <td colSpan="3">{header.accountingYear || ''}</td> */}
            </tr>
            <tr>
              <td>4. Ind. Code as per Frame<br/>(4-digit level of NIC-2008)</td>
              <td>{header.indCodeFrame}</td>
              <td>12. Accounting Year ( MMYY to MMYY )</td>
              <td>{header.accountingYear || ''}</td>
              {/* <td>13. Name of SSO:</td> */}
              {/* <td colSpan="3"></td> */}
              <td>18. Personal Code</td>
              <td>Uttar Pradesh</td>
            </tr>
            <tr>
              <td>5. Ind. Code as per Return<br/>(5-digit level of NIC-2008)</td>
              <td>{header.indCodeReturn}</td>
              <td>13. Name of SSO:</td>
              <td>{ssoName || ''}</td>
              <td colSpan="2"></td>
            </tr>
            <tr>
              <td>6. Description of Industry</td>
              <td colSpan="5">{header.nameUnit}</td>
            </tr>
            <tr>
              <td>7. State Code</td>
              <td>{header.state || '09'}</td>
              <td>14. Personal Code</td>
              <td></td>
              <td>19. Head Quarter:</td>
              {/* <td>20. Signature.</td> */}
              <td></td>
            </tr>
            <tr>
              <td>8. District Code</td>
              <td>{header.district || '09'}</td>
              <td>15. Head Quarter:</td>
              <td>Uttar Pradesh</td>
              <td>20. Signature.</td>
              {/* <td>9. RO/SRO Code</td> */}
              {/* <td>{header.roSroCode || ''}</td> */}
            </tr>
            <tr>
              <td colSpan="1">Name and address of the Industrial Undertaking</td>
              <td colSpan="5">{header.nameUnit}</td>
            </tr>
            <tr>
              <td>town:</td>
              <td>{header.town}</td>
              <td>Tehsil / Taluk:</td>
              <td>{header.tehsil}</td>
              <td>district:</td>
              <td>{header.district}</td>
            </tr>
          </tbody>
        </table>

        {/* Block 2 */}
        <table className="report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th colSpan="12" className="section-heading">block 2. – Mandays worked, absenteeism, labour turnover for regular workers directly employed for each month of the year</th>
            </tr>
            <tr>
              <th style={{ width: '5%' }}>Sl No</th>
              <th style={{ width: '9%' }}>Month / Year</th>
              <th style={{ width: '9%' }}>Scheduled Working Days for Workers</th>
              <th style={{ width: '9%' }}>No. Mandays Worked</th>
              <th style={{ width: '9%' }}>No. Mandays lost due to absence</th>
              <th style={{ width: '9%' }}>No. of Workers in Employment on the First day</th>
              <th style={{ width: '9%' }}>No. of Workers in Employment on the Last day</th>
              <th style={{ width: '8%' }}>Accession during the Month</th>
              <th style={{ width: '8%' }}>Separation during the Month</th>
              <th style={{ width: '8%' }}>Separation during the Month due to death or retirement</th>
              <th style={{ width: '8%' }}>Separation during the Month due to other reasons</th>
              <th style={{ width: '9%' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m, idx) => {
              const r = monthRows[m] || {};
              return (
                <tr key={m}>
                  <td style={{ textAlign:'center' }}>{idx + 1}</td>
                  <td style={{ textAlign:'center' }}>{m}</td>
                  <td style={{ textAlign:'center' }}>{r.scheduled || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.mandaysWorked || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.lostAbsence || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.firstDay || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.lastDay || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.accession || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.sepMonth || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.sepDeath || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.sepOther || ''}</td>
                  <td style={{ textAlign:'center' }}>{r.remarks || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
          </div>
        </div>

{/* Action Buttons */}
<div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/scrutiny-sheet')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>

      </div>
    </>
  );
}

export default PartII;